import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { usePipelineContextSafe } from '../../context';
import { PipelineStage } from './PipelineStage';
import { OutcomesRow } from './OutcomesRow';
import { PipelineLoadingSkeleton } from '../PipelineLayout/PipelineLoadingSkeleton';
import type { PipelineStage as PipelineStageType } from '../../types';

interface PipelineBoardProps {
  children?: React.ReactNode;
}

/**
 * Pipeline Board - Horizontal kanban layout for pipeline stages
 *
 * Layout:
 * - Main stages scroll horizontally (1st Contact → Won)
 * - Outcome stages (Lost, Not Qualified, Grant) in collapsible bottom row
 * - Stages use fixed widths (320px expanded, 48px collapsed) with horizontal scroll
 */
export function PipelineBoard({ children }: PipelineBoardProps) {
  // Use safe context hook - returns null if outside provider (e.g., portal edge case)
  const context = usePipelineContextSafe();

  // All hooks MUST run before any early return (Rules of Hooks).
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const stages = context?.state.stages ?? [];
  const pipelineLayout = context?.state.pipelineLayout;

  // Organize stages into main pipeline and outcomes
  const { mainStages, outcomeStages } = useMemo(() => {
    const sortedStages = [...stages].sort((a, b) => a.order - b.order);

    const outcomes = sortedStages.filter(
      (stage) =>
        stage.name === 'Lost' ||
        stage.name === 'Not Qualified' ||
        stage.name === 'Grant'
    );

    const main = sortedStages.filter(
      (stage) =>
        stage.name !== 'Lost' &&
        stage.name !== 'Not Qualified' &&
        stage.name !== 'Grant'
    );

    return { mainStages: main, outcomeStages: outcomes };
  }, [stages]);

  const handleScroll = useCallback(() => {
    if (!hasScrolled && scrollContainerRef.current) {
      if (scrollContainerRef.current.scrollLeft > 20) {
        setHasScrolled(true);
      }
    }
  }, [hasScrolled]);

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        setHasOverflow(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [mainStages.length]);

  // Early returns now live after all hooks.
  if (!context) {
    return <PipelineLoadingSkeleton />;
  }

  if (children) {
    return <div className="relative">{children}</div>;
  }

  return (
    <div className="relative flex flex-col" style={{ height: 'calc(100vh - 160px)', minHeight: '500px' }}>
      {/* Main pipeline - horizontal scroll */}
      <div className="relative flex-1 overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-x-auto overflow-y-hidden pb-2"
          onScroll={handleScroll}
          style={{
            scrollbarWidth: 'thin',
            scrollBehavior: 'smooth',
          }}
        >
          <div
            className={`flex flex-row flex-nowrap h-full ${
              pipelineLayout === 'compact' ? 'gap-3' : 'gap-4'
            }`}
            style={{
              // Let flexbox calculate width naturally from child elements
              // This allows collapsed stages (48px) and expanded stages (320px) to size correctly
              paddingRight: '16px',
            }}
          >
            {mainStages.map((stage) => (
              <PipelineStage key={stage.id} stage={stage} />
            ))}
          </div>
        </div>

        {/* Scroll hint - appears until user scrolls */}
        {!hasScrolled && hasOverflow && (
          <div
            className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-slate-100/90 dark:from-slate-900/90 to-transparent z-10 pointer-events-none flex items-center justify-end pr-2"
            style={{
              animation: 'pulse 2s ease-in-out infinite',
            }}
          >
            <div className="flex flex-col items-center gap-1 text-slate-400 dark:text-slate-500">
              <ChevronRight className="h-6 w-6 animate-bounce-x" />
              <span className="text-[10px] font-medium">Scroll</span>
            </div>
          </div>
        )}
      </div>

      {/* Collapsible outcomes row */}
      {outcomeStages.length > 0 && <OutcomesRow stages={outcomeStages} />}

      {/* CSS for bounce animation */}
      <style>{`
        @keyframes bounce-x {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(4px);
          }
        }
        .animate-bounce-x {
          animation: bounce-x 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
